import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

type ThaiInspiredLoaderProps = {
  text?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showParticles?: boolean;
};

/**
 * A professionally styled loader with subtle animations
 * Uses primarily royal blue for a clean, modern look
 */
export function ThaiInspiredLoader({
  text = 'Loading...',
  className,
  size = 'md',
  showParticles = true,
}: ThaiInspiredLoaderProps) {
  const sizeClasses = {
    sm: 'w-64 p-4',
    md: 'w-80 p-6',
    lg: 'w-96 p-8',
  };

  return (
    <div className={cn('fixed inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm z-50', className)}>
      <div
        className={cn(
          'relative bg-card/90 rounded-lg shadow-lg overflow-hidden border border-border',
          sizeClasses[size],
        )}
      >
        {/* Subtle top border */}
        <div className="absolute top-0 left-0 w-full h-[2px] bg-royal-blue-400/50"></div>

        {/* Content */}
        <div className="flex flex-col items-center justify-center py-5 px-4 space-y-5">
          {/* Text with clean, professional font */}
          <h3 className="text-base font-prompt text-foreground font-medium tracking-wide">{text}</h3>

          {/* Main loader - simplified, elegant */}
          <div className="relative w-full h-0.5 bg-muted rounded-full overflow-hidden">
            <div className="absolute inset-0 w-full">
              <div className="h-full w-1/2 bg-royal-blue-400 rounded-full animate-loader-slide"></div>
            </div>
          </div>

          {/* Minimal spinning indicator */}
          <div className="flex items-center justify-center">
            <Loader2 className="w-4 h-4 text-royal-blue-400 animate-spin mr-2 opacity-70" />
            <span className="text-xs text-muted-foreground font-sarabun opacity-70">กำลังประมวลผล...</span>
          </div>
        </div>

        {/* Reduced, subtle decorative elements */}
        {showParticles && (
          <>
            <div className="absolute top-1/4 right-6 w-1 h-1 rounded-full bg-royal-blue-300/30 animate-float-slow"></div>
            <div className="absolute bottom-1/3 left-6 w-1 h-1 rounded-full bg-royal-blue-400/30 animate-float-medium"></div>
          </>
        )}
      </div>
    </div>
  );
}

export default ThaiInspiredLoader;
